/**
 * DisclaimerToast — small-viewport variant of the AI disclaimer.
 *
 * On phones <= 414px (iPhone SE / standard mini) the persistent
 * inline-row variant crowds the mic. Instead we surface the disclaimer
 * once at the moment voice goes live, then keep a small tappable pill
 * that re-shows it on demand.
 *
 * UX rules:
 *   - Auto-shows when `active` flips true → false → true (per-session
 *     activation). Auto-dismisses after AUTO_DISMISS_MS.
 *   - Pill is a small "AI" affordance the user can tap to re-show.
 *   - Pointer-events on the toast container; pill is the only interactive
 *     element when toast is hidden.
 *
 * On larger screens this component renders nothing — Dashboard uses the
 * persistent row variant.
 */
import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import './DisclaimerToast.css';

interface DisclaimerToastProps {
  /** Server-driven text (serverHints.disclaimer). Falls back to translation. */
  text?: string;
  /** Whether voice is currently active. Controls auto-show on transition. */
  active: boolean;
}

const AUTO_DISMISS_MS = 6000;

export default function DisclaimerToast({
  text,
  active,
}: DisclaimerToastProps) {
  const { t } = useLanguage();
  const value = text ?? t('DISCLAIMER');
  const [visible, setVisible] = useState(false);
  const lastActiveRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  const show = () => {
    if (!value) return;
    setVisible(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(
      () => setVisible(false),
      AUTO_DISMISS_MS,
    );
  };

  // Auto-show on activation transition (false → true).
  useEffect(() => {
    if (active && !lastActiveRef.current) show();
    if (!active) setVisible(false);
    lastActiveRef.current = active;
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!active || !value) return null;

  return (
    <>
      {/* Toast — rendered absolutely from top of viewport */}
      <div
        className={`wg-disclaimer-toast${visible ? ' is-visible' : ''}`}
        role="status"
        aria-live="polite"
        aria-label="AI disclaimer"
      >
        <p className="wg-disclaimer-toast__text">{value}</p>
      </div>

      {/* Persistent "AI" pill that re-shows the toast on tap */}
      <button
        type="button"
        className="wg-disclaimer-pill"
        onClick={show}
        aria-label="Show AI disclaimer"
      >
        AI
      </button>
    </>
  );
}
